import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { generateDynamicSitemap, generateEnhancedSitemapXML } from '@/lib/dynamic-sitemap-generator';
import { Download, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';

interface SitemapStats {
  totalUrls: number;
  lastGenerated: string;
  categories: {
    static: number;
    destinations: number;
    places: number;
    collections: number;
    search: number;
    countries: number;
    blogs: number;
  };
}

export const DynamicSitemapGenerator: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sitemapStats, setSitemapStats] = useState<SitemapStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sitemapXml, setSitemapXml] = useState<string | null>(null);

  const generateSitemap = async () => {
    setIsGenerating(true);
    setProgress(0);
    setError(null);
    
    try {
      const baseUrl = window.location.origin;
      
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const { urls, totalUrls, lastGenerated, categories } = await generateDynamicSitemap(baseUrl);
      const xml = generateEnhancedSitemapXML(urls, baseUrl);
      
      clearInterval(progressInterval);
      setProgress(100);
      
      // Use the categories from the generator
      const stats: SitemapStats = {
        totalUrls,
        lastGenerated,
        categories
      };
      
      setSitemapStats(stats);
      setSitemapXml(xml);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate sitemap');
      console.error('Sitemap generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadSitemap = () => {
    if (!sitemapXml) return;
    
    const blob = new Blob([sitemapXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copySitemapUrl = () => {
    const sitemapUrl = `${window.location.origin}/sitemap.xml`;
    navigator.clipboard.writeText(sitemapUrl);
  };

  useEffect(() => {
    // Auto-generate on component mount
    generateSitemap();
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Dynamic Sitemap Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isGenerating && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Generating sitemap...</span>
                <span className="text-sm font-medium">{progress}%</span>
              </div>
              <Progress value={progress} className="w-full" />
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {sitemapStats && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Successfully generated sitemap with {sitemapStats.totalUrls} URLs
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button 
              onClick={generateSitemap} 
              disabled={isGenerating}
              variant="outline"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
              {isGenerating ? 'Generating...' : 'Regenerate Sitemap'}
            </Button>
            
            {sitemapXml && (
              <Button onClick={downloadSitemap}>
                <Download className="h-4 w-4 mr-2" />
                Download Sitemap
              </Button>
            )}
            
            <Button onClick={copySitemapUrl} variant="outline">
              Copy Sitemap URL
            </Button>
          </div>
        </CardContent>
      </Card>

      {sitemapStats && (
        <Card>
          <CardHeader>
            <CardTitle>Sitemap Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{sitemapStats.totalUrls}</div>
                <div className="text-sm text-gray-600">Total URLs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{sitemapStats.categories.destinations}</div>
                <div className="text-sm text-gray-600">Destinations</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{sitemapStats.categories.places}</div>
                <div className="text-sm text-gray-600">Places</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{sitemapStats.categories.collections}</div>
                <div className="text-sm text-gray-600">Collections</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{sitemapStats.categories.countries}</div>
                <div className="text-sm text-gray-600">Country Pages</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{sitemapStats.categories.blogs}</div>
                <div className="text-sm text-gray-600">Blog Posts</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-pink-600">{sitemapStats.categories.search}</div>
                <div className="text-sm text-gray-600">Search Pages</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">{sitemapStats.categories.static}</div>
                <div className="text-sm text-gray-600">Static Pages</div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t">
              <div className="text-sm text-gray-600">
                Last generated: {new Date(sitemapStats.lastGenerated).toLocaleString()}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
